import { useTabViewStateStore } from '../../store/tabViewStateStore';
import { clearAllTabScopedEntries } from '../../store/tab/tabScopedStore';
import type {
  GlobalNavigationState,
  MainContentTab,
  OpenMainContentTabInput,
  SidebarTab,
  Tab,
  TabGroup,
  TabPageEntry,
} from '../../store/layoutStoreTypes';

type CreateTabPageEntryFromOpenInput = <TType extends MainContentTab>(args: {
  tab: OpenMainContentTabInput & { type: TType };
}) => TabPageEntry;

type GetSidebarTabForMainContentTab = (
  tabType: MainContentTab,
) => SidebarTab | null;

type OnMainContentTabClosed = (args: { closingTab: Tab }) => void;

export function resetMainContentTabsForBoothHandoff({
  get,
  set,
  homeTab,
  createTabPageEntryFromOpenInput,
  getSidebarTabForMainContentTab,
  onMainContentTabClosed,
}: {
  get: () => {
    mainContentTabs: Tab[];
    tabGroups: TabGroup[];
    setSidebarTab: (tab: SidebarTab) => void;
  };
  set: (partial: {
    mainContentTabs: Tab[];
    activeMainContentTabId: string;
    tabHistory: string[];
    tabNavigationByTabId: Record<
      string,
      { entries: TabPageEntry[]; currentIndex: number }
    >;
    tabGroups: TabGroup[];
    globalNavigation: GlobalNavigationState;
    endpointTabs: Record<string, never>;
    webPageTabs: Record<string, never>;
    tabSections: Record<string, never>;
    selectedSidebarTab?: SidebarTab;
  }) => void;
  homeTab: OpenMainContentTabInput;
  createTabPageEntryFromOpenInput: CreateTabPageEntryFromOpenInput;
  getSidebarTabForMainContentTab: GetSidebarTabForMainContentTab;
  onMainContentTabClosed?: OnMainContentTabClosed;
}): void {
  const { mainContentTabs, tabGroups } = get();
  const homeTabId = homeTab.id;

  for (const tab of mainContentTabs) {
    if (tab.id === homeTabId) {
      continue;
    }
    useTabViewStateStore.getState().clearTabState(tab.id);
    clearAllTabScopedEntries(tab.id);
    onMainContentTabClosed?.({ closingTab: tab });
  }

  const activatedHomeTab = { ...homeTab, isActive: true } as Tab;
  const homePageEntry = createTabPageEntryFromOpenInput({ tab: homeTab });
  const sidebarTab =
    getSidebarTabForMainContentTab(homeTab.type) ?? 'dashboard';

  get().setSidebarTab(sidebarTab);

  const nextTabGroups = tabGroups
    .map((group) => ({
      ...group,
      tabIds: group.tabIds.filter((tabId) => tabId === homeTabId),
    }))
    .filter((group) => group.tabIds.length > 0);

  set({
    mainContentTabs: [activatedHomeTab],
    activeMainContentTabId: homeTabId,
    tabHistory: [homeTabId],
    tabNavigationByTabId: {
      [homeTabId]: {
        entries: [homePageEntry],
        currentIndex: 0,
      },
    },
    tabGroups: nextTabGroups,
    globalNavigation: {
      entries: [{ tabId: homeTabId, pageEntry: homePageEntry }],
      currentIndex: 0,
    },
    endpointTabs: {},
    webPageTabs: {},
    tabSections: {},
    selectedSidebarTab: sidebarTab,
  });
}
